"use client";

import React, { useState } from "react";
import { CATEGORY_A_ELECTIVE, CATEGORY_B_SUBJECTS, CATEGORY_C_SUBJECTS } from "@/constants";
import { getSubjectCategory } from "@/lib/utils/subjectCategories";
import { getSubjectDisplayName } from "@/lib/data/subjects/subjects";

interface AddNewElectiveProps {
    selectedElectives: string[];
    onAddSubject: (subject: string) => void;
    language: string;
    translate: (key: string) => string;
    className?: string;
}

export const AddNewElective: React.FC<AddNewElectiveProps> = ({
    selectedElectives,
    onAddSubject,
    language,
    translate,
    className = "",
}) => {
    const [subjectSearchQuery, setSubjectSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<'All' | 'A' | 'B' | 'C'>('All');
    const [showAutocomplete, setShowAutocomplete] = useState(false);
    const [selectedSubjectIndex, setSelectedSubjectIndex] = useState(0);

    const electiveSubjectsByCategory: Record<'All' | 'A' | 'B' | 'C', string[]> = {
        All: [...CATEGORY_A_ELECTIVE, ...CATEGORY_B_SUBJECTS, ...CATEGORY_C_SUBJECTS],
        A: CATEGORY_A_ELECTIVE,
        B: CATEGORY_B_SUBJECTS,
        C: CATEGORY_C_SUBJECTS,
    };

    // Filter available subjects by category and exclude already selected
    const availableSubjects = electiveSubjectsByCategory[selectedCategory]
        .filter((s: string) => !selectedElectives.includes(s))
        .sort((a: string, b: string) => a.localeCompare(b));

    // Further filter by search query
    const filteredAvailableSubjects = subjectSearchQuery.trim() === ''
        ? availableSubjects
        : availableSubjects.filter((s: string) =>
            s.toLowerCase().includes(subjectSearchQuery.toLowerCase())
        );

    // Add a new subject
    const handleAddSubject = (subject: string) => {
        if (subject && availableSubjects.includes(subject)) {
            onAddSubject(subject);
            setSubjectSearchQuery('');
            setShowAutocomplete(false);
            setSelectedSubjectIndex(0);
        }
    };

    // Keyboard navigation for subject autocomplete
    const handleSubjectKeyDown = (e: React.KeyboardEvent) => {
        if (!showAutocomplete) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedSubjectIndex(prev =>
                    prev < filteredAvailableSubjects.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedSubjectIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (filteredAvailableSubjects.length > 0) {
                    handleAddSubject(filteredAvailableSubjects[selectedSubjectIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setShowAutocomplete(false);
                break;
        }
    };

    // Calculate subject counts for each category
    const getCategoryCount = (cat: 'All' | 'A' | 'B' | 'C'): number => {
        return electiveSubjectsByCategory[cat].filter((s: string) => !selectedElectives.includes(s)).length;
    };

    return (
        <div className={"p-4 border rounded-lg bg-white/80 backdrop-blur-sm shadow-lg " + className}>
                {/* Category Filter */}
                <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">{translate('addNewElectiveSubject')}</label>
                    <div className="flex gap-2">
                        {(['All', 'A', 'B', 'C'] as const).map(cat => (
                            <button
                                key={cat}
                                onClick={() => {
                                    setSelectedCategory(cat);
                                    setSubjectSearchQuery('');
                                    setSelectedSubjectIndex(0);
                                }}
                                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${selectedCategory === cat
                                    ? cat === 'A' ? 'bg-blue-600 text-white' :
                                        cat === 'B' ? 'bg-amber-600 text-white' :
                                            cat === 'C' ? 'bg-purple-600 text-white' :
                                                'bg-gray-800 text-white'
                                    : cat === 'A' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                                        cat === 'B' ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' :
                                            cat === 'C' ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' :
                                                'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {cat === 'All'
                                    ? `${translate('all')} (${getCategoryCount(cat)})`
                                    : `${translate('categoryShort')} ${cat} (${getCategoryCount(cat)})`}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search Input with Autocomplete */}
                <div className="relative">

                    <input
                        
                        type="text"

                        value={subjectSearchQuery}

                        onChange={(e) => {
                            setSubjectSearchQuery(e.target.value);
                            setShowAutocomplete(true);
                            setSelectedSubjectIndex(0);
                        }}

                        onFocus={() => setShowAutocomplete(true)}

                        onBlur={() => {
                            // Delay to allow click on dropdown item
                            setTimeout(() => setShowAutocomplete(false), 200);
                        }}

                        onKeyDown={handleSubjectKeyDown}

                        placeholder={availableSubjects.length > 0 ? translate('searchAndSelectSubject') : translate('noSubjectsAvailable')}

                        disabled={availableSubjects.length === 0}

                        className="mt-2 w-full border border-gray-300 rounded-lg p-3 text-sm bg-white text-black focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    />

                    {/* Autocomplete Dropdown */}
                    {showAutocomplete && filteredAvailableSubjects.length > 0 && (
                        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                            {filteredAvailableSubjects.map((subject, idx) => {
                                const category = getSubjectCategory(subject);
                                return (
                                    <div
                                        key={subject}
                                        onClick={() => handleAddSubject(subject)}
                                        onMouseEnter={() => setSelectedSubjectIndex(idx)}
                                        className={`px-4 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center gap-2 ${idx === selectedSubjectIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                    >
                                        <span className={`px-2 py-0.5 rounded text-xs font-medium w-14 text-center ${category === 'A' ? 'bg-blue-100 text-blue-700' : category === 'B' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                            {translate('categoryShort')} {category}
                                        </span>
                                        <span className="text-sm text-gray-900">{getSubjectDisplayName(subject, language)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* No Results Message */}
                    {showAutocomplete && subjectSearchQuery.trim() !== '' && filteredAvailableSubjects.length === 0 && (
                        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl px-4 py-3">
                            <span className="text-sm text-gray-500">{translate('noSubjectsFoundMatching')} &quot;{subjectSearchQuery}&quot;</span>
                        </div>
                    )}
                </div>
        </div>
    );
};
